import { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";
import { Button } from "@/components/ui/button";
import {
  ArrowRight,
  ChevronRight,
  MapPin,
  Calendar,
  Building2,
  Users,
  Layers,
  Quote,
  Star,
  TrendingUp,
  CheckCircle2,
  ArrowUpRight,
} from "lucide-react";
import { useLeadCapture } from "@/context/LeadCaptureContext";
import { resolveCaseStudy, CASE_STUDIES } from "@/data/caseStudies";

export default function CaseStudyPage() {
  const { slug } = useParams();
  const study = resolveCaseStudy(slug);
  const { openLeadCapture } = useLeadCapture();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!study) {
    return <Navigate to="/" replace />;
  }

  const ctaSource = `Kundberättelse · ${study.business}`;
  const otherStudies = Object.values(CASE_STUDIES)
    .filter((s) => s.slug !== study.slug)
    .slice(0, 3);

  return (
    <div
      data-testid={`case-study-${study.slug}`}
      className="min-h-screen bg-white dark:bg-slate-950 text-slate-900 dark:text-slate-100"
    >
      <Seo
        title={study.business}
        description={study.seoDescription}
        path={`/kunder/${study.slug}`}
        image={study.hero}
        type="article"
      />
      <Navbar />

      <main className="pt-28">
        {/* Breadcrumb */}
        <div className="mx-auto max-w-5xl px-6 lg:px-8">
          <nav className="flex items-center text-xs text-slate-500 dark:text-slate-400 gap-1 flex-wrap">
            <Link to="/" className="hover:text-emerald-600 dark:hover:text-emerald-400">
              Hem
            </Link>
            <ChevronRight className="h-3 w-3" />
            <span>Kundberättelser</span>
            <ChevronRight className="h-3 w-3" />
            <Link
              to={`/lokalt/${study.citySlug}`}
              data-testid="case-breadcrumb-city"
              className="hover:text-emerald-600 dark:hover:text-emerald-400"
            >
              {study.city}
            </Link>
            <ChevronRight className="h-3 w-3" />
            <span className="text-slate-700 dark:text-slate-200 truncate max-w-[260px]">
              {study.business}
            </span>
          </nav>
        </div>

        {/* HERO */}
        <section className="relative overflow-hidden py-16 md:py-20">
          <div className="absolute -top-24 -right-20 h-[420px] w-[420px] blob-emerald opacity-50 pointer-events-none" />
          <div className="mx-auto max-w-5xl px-6 lg:px-8">
            <div className="inline-flex items-center gap-2 rounded-full border border-emerald-500/20 bg-emerald-500/5 dark:bg-emerald-500/10 px-3 py-1.5 text-xs font-semibold tracking-[0.18em] uppercase text-emerald-700 dark:text-emerald-300">
              Kundberättelse · {study.industry}
            </div>
            <h1
              data-testid="case-headline"
              className="font-display mt-6 text-4xl sm:text-5xl lg:text-[56px] leading-[1.05] font-bold tracking-tight text-slate-900 dark:text-white"
            >
              {study.headline}
            </h1>
            <p
              data-testid="case-summary"
              className="mt-6 text-xl text-slate-600 dark:text-slate-300 leading-relaxed max-w-3xl"
            >
              {study.summary}
            </p>

            {/* Fact strip */}
            <dl className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
              <Fact icon={Building2} label="Verksamhet" value={study.business} />
              <Fact icon={MapPin} label="Ort" value={study.city} link={`/lokalt/${study.citySlug}`} testid="case-city-link" />
              <Fact icon={Users} label="Storlek" value={study.size} />
              <Fact icon={Calendar} label="Hos Hundify" value={study.customerSince} />
            </dl>
          </div>
        </section>

        {/* Hero image */}
        <section className="mx-auto max-w-5xl px-6 lg:px-8">
          <div className="relative rounded-[28px] overflow-hidden aspect-[16/9] shadow-2xl ring-1 ring-slate-200 dark:ring-slate-800">
            <img src={study.hero} alt={study.business} className="h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-950/40 to-transparent" />
          </div>
        </section>

        {/* Results strip */}
        <section className="py-16">
          <div className="mx-auto max-w-5xl px-6 lg:px-8">
            <div
              data-testid="case-results"
              className="grid sm:grid-cols-3 gap-4 rounded-3xl border border-emerald-200 dark:border-emerald-500/20 bg-emerald-50 dark:bg-emerald-500/5 p-2"
            >
              {study.results.map((r, i) => (
                <div
                  key={r.label}
                  data-testid={`case-result-${i}`}
                  className="rounded-2xl bg-white dark:bg-slate-900 p-6 text-center"
                >
                  <div className="flex justify-center text-emerald-500 mb-2">
                    <TrendingUp className="h-5 w-5" />
                  </div>
                  <div className="font-display text-4xl font-bold text-slate-900 dark:text-white">
                    {r.value}
                  </div>
                  <div className="mt-1 text-xs uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400">
                    {r.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Body */}
        <article className="mx-auto max-w-3xl px-6 lg:px-8 pb-16 space-y-14">
          <BodySection title={study.challenge.title} paragraphs={study.challenge.paragraphs} kicker="01" />

          {/* Pull quote */}
          <figure className="relative rounded-3xl bg-slate-900 dark:bg-slate-950 text-white p-10 md:p-12">
            <Quote className="absolute -top-5 -left-3 h-12 w-12 text-emerald-500" fill="currentColor" />
            <div className="flex gap-1 mb-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star key={i} className="h-4 w-4 fill-emerald-400 text-emerald-400" />
              ))}
            </div>
            <blockquote
              data-testid="case-pull-quote"
              className="font-display text-2xl md:text-3xl leading-snug font-semibold"
            >
              &ldquo;{study.quote.text}&rdquo;
            </blockquote>
            <figcaption className="mt-8 flex items-center gap-4 pt-6 border-t border-white/10">
              <div className="h-12 w-12 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 text-white font-display font-bold text-lg flex items-center justify-center shadow-md">
                {study.person.initial}
              </div>
              <div>
                <div className="font-semibold">{study.person.name}</div>
                <div className="text-sm text-slate-400">
                  {study.person.role} · {study.business}
                </div>
                <div className="text-xs text-emerald-400 mt-0.5 inline-flex items-center gap-1 font-medium">
                  <MapPin className="h-3 w-3" /> {study.area}
                </div>
              </div>
            </figcaption>
          </figure>

          <BodySection
            title={study.solution.title}
            paragraphs={study.solution.paragraphs}
            bullets={study.solution.bullets}
            kicker="02"
          />

          {/* Modules used */}
          <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 p-6">
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-emerald-600 dark:text-emerald-400 inline-flex items-center gap-2">
              <Layers className="h-3.5 w-3.5" />
              Moduler i bruk
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {study.modules.map((m) => (
                <span
                  key={m}
                  className="inline-flex items-center text-sm font-medium rounded-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 px-3 py-1.5 text-slate-700 dark:text-slate-200"
                >
                  {m}
                </span>
              ))}
            </div>
          </div>
        </article>

        {/* CTA */}
        <section className="py-20">
          <div className="mx-auto max-w-5xl px-6 lg:px-8">
            <div className="rounded-3xl bg-gradient-to-br from-emerald-500 to-emerald-600 text-white p-10 md:p-14 relative overflow-hidden">
              <div className="absolute inset-0 bg-dotted opacity-20" />
              <div className="relative grid md:grid-cols-2 gap-8 items-center">
                <div>
                  <h2 className="font-display text-3xl md:text-4xl font-bold leading-tight">
                    Vill du ha samma resultat?
                  </h2>
                  <p className="mt-4 text-emerald-50/90 max-w-lg">
                    Boka en kostnadsfri demo med vårt team. Vi visar exakt hur Hundify
                    skulle kunna fungera för din verksamhet i {study.city} — och svarar
                    på alla frågor.
                  </p>
                </div>
                <div className="flex flex-col gap-3 md:items-end">
                  <Button
                    onClick={() => openLeadCapture(ctaSource)}
                    data-testid="case-bottom-cta"
                    className="h-12 px-6 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-semibold w-full md:w-auto"
                  >
                    Boka en demo <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                  <Link to={`/lokalt/${study.citySlug}`}>
                    <Button
                      variant="outline"
                      data-testid="case-back-to-city"
                      className="h-12 px-6 rounded-xl bg-white/10 border-white/30 text-white hover:bg-white/20 hover:text-white w-full md:w-auto"
                    >
                      Tillbaka till Hundify i {study.city}
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Other studies */}
        <section className="py-20 border-t border-slate-200 dark:border-slate-800 bg-slate-50/60 dark:bg-slate-900/30">
          <div className="mx-auto max-w-5xl px-6 lg:px-8">
            <div className="max-w-2xl">
              <div className="text-xs uppercase tracking-[0.2em] font-semibold text-emerald-600 dark:text-emerald-400">
                Fler kundberättelser
              </div>
              <h2 className="font-display mt-3 text-3xl font-bold text-slate-900 dark:text-white">
                Läs hur andra verksamheter använder Hundify
              </h2>
            </div>
            <div
              data-testid="other-cases-grid"
              className="mt-10 grid md:grid-cols-3 gap-6"
            >
              {otherStudies.map((s) => (
                <Link
                  key={s.slug}
                  to={`/kunder/${s.slug}`}
                  data-testid={`other-case-link-${s.slug}`}
                  className="group flex flex-col rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden hover:-translate-y-1 hover:shadow-xl transition-all"
                >
                  <div className="aspect-[16/9] overflow-hidden">
                    <img
                      src={s.hero}
                      alt={s.business}
                      className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="flex flex-col flex-1 p-5">
                    <div className="text-xs uppercase tracking-[0.18em] font-semibold text-emerald-600 dark:text-emerald-400">
                      {s.city} · {s.industry}
                    </div>
                    <h3 className="font-display mt-2 text-lg font-semibold text-slate-900 dark:text-white leading-tight">
                      {s.business}
                    </h3>
                    <p className="mt-3 text-sm text-slate-600 dark:text-slate-300 line-clamp-3">
                      {s.summary}
                    </p>
                    <div className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-emerald-600 dark:text-emerald-400 group-hover:gap-2 transition-all">
                      Läs hela storyn <ArrowUpRight className="h-4 w-4" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

function Fact({ icon: Icon, label, value, link, testid }) {
  const content = (
    <>
      <div className="flex items-center gap-1.5 text-emerald-600 dark:text-emerald-400">
        <Icon className="h-3.5 w-3.5" />
        <span className="text-[10px] uppercase tracking-wider font-semibold">{label}</span>
      </div>
      <div className="font-display text-base font-semibold text-slate-900 dark:text-white mt-1">
        {value}
      </div>
    </>
  );
  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4">
      {link ? (
        <Link to={link} data-testid={testid} className="block hover:opacity-80 transition-opacity">
          {content}
        </Link>
      ) : (
        content
      )}
    </div>
  );
}

function BodySection({ title, paragraphs, bullets, kicker }) {
  return (
    <section>
      <div className="flex items-baseline gap-3 mb-5">
        <span className="font-display text-emerald-500 font-bold text-sm tracking-wider">{kicker}</span>
        <h2 className="font-display text-3xl font-bold text-slate-900 dark:text-white">
          {title}
        </h2>
      </div>
      <div className="space-y-4 text-lg text-slate-600 dark:text-slate-300 leading-relaxed">
        {paragraphs.map((p, i) => (
          <p key={i}>{p}</p>
        ))}
      </div>
      {bullets && (
        <ul className="mt-6 space-y-3">
          {bullets.map((b) => (
            <li
              key={b}
              className="flex items-start gap-3 text-slate-700 dark:text-slate-200"
            >
              <CheckCircle2 className="mt-1 h-5 w-5 text-emerald-500 shrink-0" />
              <span>{b}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
